import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileManagerErrorMessageComponent } from '@shared/components/file-manager-error-message/file-manager-error-message.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FileManagerErrorMessageComponent,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionComponent,
    FileManagerComponent,
    FileViewerComponent,
  ],
  selector: 'aux-import-site-amendments-dialog',
  templateUrl: './import-site-amendments-dialog.component.html',
})
export class ImportSiteAmendmentsDialogComponent {
  ref = inject(
    CustomOverlayRef<
      unknown,
      {
        protocolVersionOptions: Option[];
      }
    >
  );

  private apiService = inject(ApiService);
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  errorMessage = signal('');

  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  loading$ = new BehaviorSubject(false);
  metadata: FileMetadata = {};
  protocolVersionOptions: Option[] = [];
  selectedProtocol = new UntypedFormControl(null);

  constructor() {
    if (this.ref.data) {
      this.protocolVersionOptions = this.ref.data.protocolVersionOptions;
    }
  }

  async downloadSiteAmendmentTemplate() {
    this.errorMessage.set('');

    this.loading$.next(true);

    const payloadString = JSON.stringify({
      patient_protocol_version_id: this.selectedProtocol.value,
    });

    const { data, success } = await this.apiService.getTemplatePath(
      null,
      TemplateType.SITE_AMENDMENT_TEMPLATE,
      payloadString
    );

    if (!(success && data)) {
      this.overlayService.error(MessagesConstants.FILE.PROBLEM_DOWNLOADING_TEMPLATE);
    } else {
      await this.apiService.downloadFileFromPath(data.id);
    }

    this.loading$.next(false);
  }

  getFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/site-amendments/`;
  }

  async onUpload() {
    this.errorMessage.set('');

    if (this.fileManager && !this.loading$.getValue()) {
      const files = this.fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      this.loading$.next(true);

      const file = files[0];

      const trialId = this.mainQuery.getValue().trialKey;

      const filePath = this.getFilePath();

      const key = `${filePath}${file.bucket_key}`;

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: key,
      });

      const fileSuccess = await this.fileManager.fileService.uploadFiles({
        metadata: { admin: '1' },
      });

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${filePath}${file.bucket_key}`,
            entity_id: trialId,
            entity_type: EntityType.TRIAL,
            type: EventType.SITE_AMENDMENT_TEMPLATE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success('Site Amendments updated');
        } else {
          void this.apiService.removeFile(`${this.getFilePath()}${file.bucket_key}`);
          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  pathFn: () => string = () => '';
}
